import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CREATE_DEAL_TON,
  DEFER_FEE_PCT,
  deferFeeFor,
  MAX_FEE_BPS,
  MIN_PAYMENT,
  PAYMENT_GAS,
  forfeitTable,
  validateTerms,
  forwardOn,
  graceOf,
  refundPercentOf,
  type DealTerms,
  type RefundTier,
} from '@78/core';
import { CURRENCY } from '@78/i18n';
import { lastWalletRequest, useActions } from '../lib/actions.ts';
import { fetchCollections, fetchRegistry, fetchWalletDeals } from '../lib/api.ts';
import { Sheet } from '../components/Sheet.tsx';
import { GasNote } from '../components/GasNote.tsx';
import { fetchOwnedNfts, getNftIndex, getNftOwner, getTonBalance, type OwnedNft } from '../lib/chain.ts';
import { useErrorText } from '../lib/errors.ts';
import { reportFailure } from '../lib/report.ts';
import { sameAddress, shortAddress, useCollectionNames } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';

/** Цены человек называет целыми монетами, контракт держит нанотоны. */
const TON_UNIT = 1_000_000_000n;
const TIERS: RefundTier[] = [0, 1, 2, 3];


/** Сколько строк таблицы срыва видно без разворота. */
const SHORT_TABLE = 6;

/**
 * Конструктор оффера.
 *
 * Предпросмотр считается тем же кодом, что и контракт. Показывать здесь
 * приблизительные числа нельзя: продавец принимает решение об условиях
 * именно по ним, а увидит потом настоящие.
 */
export function Builder({
  masterAddress,
  preselect = null,
  onPublished,
}: {
  masterAddress: string;
  /**
   * Вещь, с карточки которой пришли из «Моего». Выбор остаётся и здесь:
   * в конструктор попадают и без неё — например, вернувшись назад.
   */
  preselect?: string | null;
  /** Куда идти за вторым шагом — залогом NFT. */
  onPublished: () => void;
}) {
  const { t } = useTranslation();
  const { sum, money, duration } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();

  const [price, setPrice] = useState(1000);
  const [downPct, setDownPct] = useState(30);
  const [payments, setPayments] = useState(4);
  const [weeks, setWeeks] = useState(1);
  const [tier, setTier] = useState<RefundTier>(2);
  const [deferrals, setDeferrals] = useState(2);
  const [allSteps, setAllSteps] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [nft, setNft] = useState<OwnedNft | null>(null);
  /**
   * Шаг сопровождения публикации.
   *
   * Оффер — это две подписи подряд: сначала мастер разворачивает контракт
   * сделки, потом гифт уходит в него. Между ними человека раньше отпускали
   * одного, и второй шаг терялся. Шторка держит его за руку до конца —
   * но не запирает: «позже» уводит на прежний путь, через «Сделки».
   */
  const [flow, setFlow] = useState<
    null | 'waiting' | 'ready' | 'locking' | 'locked' | 'timeout'
  >(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  /** Список допущенных коллекций: отдельной шторкой, с поиском. */
  const [showAllowed, setShowAllowed] = useState(false);
  const [search, setSearch] = useState('');
  /**
   * Срок опубликованной сделки — по нему мы её и узнаём среди своих.
   *
   * Одного гифта мало: на том же гифте может висеть прошлый оффер, у
   * которого гифт тоже не в залоге. Заложить гифт не в ту сделку — значит
   * отдать его на чужих условиях. Срок считается от времени публикации с
   * точностью до секунды и различает их надёжно.
   */
  const [awaited, setAwaited] = useState<number | null>(null);

  // Гифт выбирается из своих, а не вводится адресом: адрес айтема человек не
  // знает, а ошибка в нём отправила бы гифт в чужой контракт.
  const gifts = useQuery({
    queryKey: ['myNfts', actions.myAddress],
    queryFn: () => fetchOwnedNfts(actions.myAddress),
    enabled: actions.connected,
  });

  // Реестр решает, что вообще можно заложить: мастер отвергнет гифт из
  // коллекции вне whitelist, и человек заплатит газ за отказ. Поэтому
  // выбирать он должен из допущенных, а не из всех своих.
  const registry = useQuery({ queryKey: ['registry'], queryFn: fetchRegistry, staleTime: 60_000 });

  /**
   * Хватит ли на газ.
   *
   * Спрашиваем до отправки, а не узнаём из отказа кошелька: кошелёк в этом
   * случае возвращает ошибку без кода и без текста, SDK превращает её в
   * «неизвестную», и человек читает «отказ без объяснения», хотя причина
   * простая и чинится пополнением. Ровно так и случилось на живом
   * кошельке с 0,09 GRAM при нужных 0,3.
   */
  const balance = useQuery({
    queryKey: ['balance', actions.myAddress],
    queryFn: () => getTonBalance(actions.myAddress),
    enabled: actions.connected,
    staleTime: 30_000,
  });
  const enough = balance.data == null || balance.data >= CREATE_DEAL_TON + PAYMENT_GAS;

  /**
   * Нет ли по этой вещи уже начатого оффера.
   *
   * Оффер создаётся в два шага, и между ними NFT остаётся в кошельке. Без
   * этой проверки человек, не дошедший до передачи, приходил сюда снова и
   * выставлял ту же вещь заново: мастер разворачивал вторую сделку — 0,3
   * GRAM на контракт, который уже не понадобится, — а залог потом уходил
   * в одну из двух, и какая из них та самая, человек знать не мог.
   */
  const myDeals = useQuery({
    queryKey: ['myDeals', actions.myAddress],
    queryFn: () => fetchWalletDeals(actions.myAddress),
    enabled: actions.connected,
  });
  const nameOf = useCollectionNames();
  // Пришли с конкретной вещью — на ней и открываемся, как только список
  // кошелька доедет.
  useEffect(() => {
    // Строка, а не что угодно: обработчик клика легко передаёт сюда
    // событие, и разбор такого «адреса» роняет экран целиком.
    if (typeof preselect !== 'string' || preselect === '' || nft !== null) return;
    const found = (gifts.data ?? []).find((g) => sameAddress(g.address, preselect));
    if (found !== undefined) setNft(found);
  }, [preselect, nft, gifts.data]);

  const allowed = registry.data?.collections ?? [];
  const query = search.trim().toLowerCase();
  const shownAllowed = allowed.filter((c) => {
    if (query === '') return true;
    const name = nameOf(c.address).name ?? '';
    return name.toLowerCase().includes(query) || c.address.toLowerCase().includes(query);
  });
  const mine = gifts.data ?? [];
  const inRegistry = useMemo(() => {
    const allowed = registry.data?.collections ?? [];
    return mine.filter(
      (g) => g.collection !== null && allowed.some((c) => sameAddress(c.address, g.collection!)),
    );
  }, [mine, registry.data]);
  // Выставленный на продажу заложить нельзя: им владеет контракт площадки.
  // Считаем его отдельно от «не той коллекции» — это разные препятствия, и
  // убираются они по-разному.
  const eligible = inRegistry.filter((g) => !g.onSale);
  const onSale = inRegistry.length - eligible.length;
  const hidden = mine.length - inRegistry.length;
  const chosen = nft ?? eligible[0] ?? null;
  const startedDeal =
    chosen === null
      ? undefined
      : (myDeals.data?.deals ?? []).find(
          (d) => d.state === 0 && sameAddress(d.nftAddress, chosen.address),
        );

  // Флор коллекции выбранного гифта — из каталога: он и есть ориентир цены.
  const catalog = useQuery({ queryKey: ['collections'], queryFn: fetchCollections, staleTime: 60_000 });
  const floor = (() => {
    if (chosen?.collection == null) return null;
    const row = catalog.data?.collections.find((c) => sameAddress(c.collection, chosen.collection!));
    return row?.floor == null ? null : BigInt(row.floor);
  })();

  const terms: DealTerms = useMemo(() => {
    const total = BigInt(price) * TON_UNIT;
    return {
      totalPrice: total,
      upfront: (total * BigInt(downPct)) / 100n,
      deferFee: deferFeeFor(total, (total * BigInt(downPct)) / 100n, payments),
      period: weeks * 7 * 86400,
      nPayments: payments,
      refundTier: tier,
      maxDeferrals: deferrals,
      // Комиссия — та, что стоит в мастере, а не потолок из кода. Потолок
      // остаётся запасным ответом: пока реестр не доехал, считать по нему
      // безопаснее — он завышает удержание, а не занижает.
      feeBps: registry.data?.feeBps ?? MAX_FEE_BPS,
      deadline: Math.floor(Date.now() / 1000) + (payments + deferrals) * weeks * 7 * 86400 + 86400,
    };
  }, [price, downPct, payments, weeks, tier, deferrals, registry.data?.feeBps]);

  const installment = (terms.totalPrice - terms.upfront) / BigInt(terms.nPayments);
  const fromUpfront = forwardOn(terms, 0n, terms.upfront);
  const fromPayment = forwardOn(terms, terms.upfront, installment);
  const liveTotal = fromUpfront + fromPayment * BigInt(terms.nPayments);
  const atClosing = terms.totalPrice - (terms.totalPrice * BigInt(terms.feeBps)) / 10000n - liveTotal;
  const rows = forfeitTable(terms);

  // Проверяем те же границы, что и мастер: иначе человек заплатит газ за
  // сделку, которую контракт отвергнет без объяснения причины.
  const problem = validateTerms(terms, Math.floor(Date.now() / 1000));

  // Ждём, пока сделка появится у индексера: адрес контракта известен только
  // ему — мастер считает его из кода и данных уже у себя.
  const appeared = useQuery({
    queryKey: ['createdDeal', actions.myAddress],
    queryFn: () => fetchWalletDeals(actions.myAddress),
    enabled: flow === 'waiting' && actions.connected,
    refetchInterval: 3000,
    gcTime: 0,
  });
  const fresh =
    chosen === null || awaited === null
      ? undefined
      : appeared.data?.deals.find(
          (d) =>
            d.state === 0 &&
            d.terms.deadline === awaited &&
            sameAddress(d.nftAddress, chosen.address),
        );

  useEffect(() => {
    if (flow === 'waiting' && fresh !== undefined) setFlow('ready');
  }, [flow, fresh]);

  // Сеть иногда задерживается дольше, чем человек готов смотреть на
  // крутилку. Признаться в этом честнее, чем крутить её вечно.
  useEffect(() => {
    if (flow !== 'waiting') return undefined;
    const timer = setTimeout(() => setFlow('timeout'), 120_000);
    return () => clearTimeout(timer);
  }, [flow]);

  /** Второй шаг: NFT уходит в контракт сделки. */
  const lock = async () => {
    if (fresh === undefined || chosen === null) return;
    setFlowError(null);
    setFlow('locking');
    try {
      // Владелец спрашивается у цепочки, а не берётся из списка: пока
      // человек заполнял условия, лот мог уехать на площадку, и тогда
      // перевод отвергнет контракт айтема — отказом без объяснения.
      const owner = await getNftOwner(chosen.address);
      if (owner !== null && !sameAddress(owner, actions.myAddress)) {
        throw new Error(t('builder.not_owner_now'));
      }
      await actions.lockNft({ nftAddress: chosen.address, dealAddress: fresh.address });
      setFlow('locked');
    } catch (e) {
      reportFailure('залог NFT', e);
      const text = errorText(e);
      setFlowError(text.length > 0 ? text : null);
      setFlow('ready');
    }
  };

  /** Шторка закрыта — прежний путь остаётся: карточка с кнопкой в «Сделки». */
  const closeFlow = () => {
    // Гифт уже в контракте: карточке «осталось передать гифт» здесь больше
    // не место, а сделке самое время показаться целиком.
    if (flow === 'locked') {
      setPublished(false);
      onPublished();
    }
    setFlow(null);
    setFlowError(null);
  };

  return (
    <div className="screen">
      {/* Что принимается — до того, как человек станет искать свой NFT в
          пустом списке и гадать, почему его там нет.
          Списком это было, пока коллекций было три. На сотне с лишним
          карточка превращается в стену, которую надо пролистать, чтобы
          добраться до собственных вещей, — поэтому строка и отдельный
          список с поиском. */}
      <div className="card col">
        <div className="label">{t('builder.allowed_collections')}</div>
        <span className="muted" style={{ fontSize: 12 }}>{t('builder.allowed_hint')}</span>
        {registry.isLoading && <div className="skeleton" style={{ minHeight: 40 }} />}
        {!registry.isLoading && allowed.length > 0 && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: 'flex-start', width: 'auto' }}
            onClick={() => setShowAllowed(true)}
          >
            {t('builder.allowed_count', { count: allowed.length })}
          </button>
        )}
        {!registry.isLoading && allowed.length === 0 && (
          <span className="muted" style={{ fontSize: 13 }}>{t('builder.registry_empty')}</span>
        )}
      </div>

      {showAllowed && (
        <Sheet
          title={t('builder.allowed_collections')}
          onConfirm={() => setShowAllowed(false)}
          onClose={() => setShowAllowed(false)}
        >
          {allowed.length > 6 && (
            <input
              className="field"
              value={search}
              placeholder={t('market.search_collection')}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className="col" style={{ gap: 7, maxHeight: '55vh', overflowY: 'auto' }}>
            {shownAllowed.map((c) => {
              const meta = nameOf(c.address);
              return (
                <div key={c.address} className="row">
                  <span className="row" style={{ justifyContent: 'flex-start', gap: 9, minWidth: 0 }}>
                    {meta.image !== null && (
                      <img src={meta.image} alt="" width={24} height={24} style={{ borderRadius: 7 }} />
                    )}
                    <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {meta.name ?? shortAddress(c.address)}
                    </span>
                  </span>
                  <span className="mono dim" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                    {shortAddress(c.address)}
                  </span>
                </div>
              );
            })}
            {shownAllowed.length === 0 && <span className="muted">{t('market.nothing_found')}</span>}
          </div>
        </Sheet>
      )}

      <div className="card col">
        <div className="label">{t('builder.pick_gift')}</div>
        <span className="muted" style={{ fontSize: 12 }}>{t('builder.only_whitelisted')}</span>
        {(gifts.isLoading || registry.isLoading) && (
          <div className="skeleton" style={{ minHeight: 60 }} />
        )}
        {!registry.isLoading && (registry.data?.collections.length ?? 0) === 0 && (
          <span className="muted" style={{ fontSize: 13 }}>{t('builder.registry_empty')}</span>
        )}
        {!gifts.isLoading && !registry.isLoading && mine.length === 0 && (
          <span className="muted" style={{ fontSize: 13 }}>{t('builder.no_gifts')}</span>
        )}
        {!gifts.isLoading &&
          !registry.isLoading &&
          mine.length > 0 &&
          eligible.length === 0 &&
          (registry.data?.collections.length ?? 0) > 0 && (
            <span className="muted" style={{ fontSize: 13 }}>
              {inRegistry.length > 0 ? t('builder.all_on_sale') : t('builder.no_gifts_in_allowed')}
            </span>
          )}
        {onSale > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {t('builder.on_sale', { count: onSale })}
          </span>
        )}
        {hidden > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {t('builder.hidden_gifts', { count: hidden })}
          </span>
        )}
        {eligible.map((item) => (
          <button
            key={item.address}
            className="pick"
            aria-selected={chosen?.address === item.address}
            onClick={() => setNft(item)}
            style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 8, textAlign: 'left' }}
          >
            {item.image !== null && (
              <img src={item.image} alt="" width={32} height={32} style={{ borderRadius: 8 }} />
            )}
            <span className="col" style={{ gap: 0 }}>
              <strong style={{ fontSize: 13 }}>{item.name ?? `#${item.index}`}</strong>
              <span className="muted" style={{ fontSize: 11 }}>{item.collectionName ?? item.collection}</span>
            </span>
          </button>
        ))}
        {gifts.data !== undefined && (
          <button className="btn btn-ghost" onClick={() => void gifts.refetch()}>
            {t('builder.reload_gifts')}
          </button>
        )}
      </div>

      <div className="card col">
        {/* Флор — настоящий, из каталога, и только если он известен. В
            макете здесь стояло «848,00», и это число показывалось всем. */}
        <label className="label">
          {/* Единица прямо в подписи поля: человек вводит целое число, и
              без неё непонятно, в чём он назначает цену. Ниже суммы уже с
              валютой, но решение он принимает здесь. */}
          {floor === null
            ? t('builder.price', { cur: CURRENCY })
            : t('builder.price_with_floor', { cur: CURRENCY, floor: money(floor) })}
        </label>
        <input
          className="mono"
          type="number"
          value={price}
          onChange={(e) => setPrice(Math.max(1, Number(e.target.value)))}
          style={{ background: 'var(--sf2)', border: '1px solid var(--line)', color: 'var(--ink)', borderRadius: 10, padding: 10, fontSize: 18 }}
        />
        <div className="row">
          <span className="muted">{t('builder.down_payment')}</span>
          <span className="num">{downPct}% · {money(terms.upfront)}</span>
        </div>
        <input type="range" min={10} max={50} value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} />
        <div className="row">
          <span className="muted">{t('builder.payments_weekly')}</span>
          <span className="num">{payments} × {money(installment)}</span>
        </div>
        <input type="range" min={1} max={52} value={payments} onChange={(e) => setPayments(Number(e.target.value))} />
      </div>

      {/* Уровень — четыре карточки, а не список: продавец должен видеть
          обмен между скоростью денег и защитой покупателя. */}
      <div className="card col">
        <div className="label">{t('builder.refund_level')}</div>
        <span className="muted" style={{ fontSize: 12 }}>{t('builder.what_buyer_gets_back')}</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {TIERS.map((x) => (
            <button
              key={x}
              className="pick"
              aria-selected={tier === x}
              onClick={() => setTier(x)}
              style={{ flexDirection: 'column', display: 'flex', gap: 2, padding: 10 }}
            >
              <strong>{String(refundPercentOf(x))}% {t('builder.buyer')}</strong>
              <span style={{ fontSize: 11, opacity: 0.75 }}>{duration(graceOf(x))}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card col">
        <div className="label">{t('builder.live_preview')}</div>
        <div className="row">
          <span className="muted">{t('builder.straight_wallet_2')}</span>
          <strong className="num">{sum(liveTotal)}</strong>
        </div>
        <div className="row">
          <span className="muted">{t('builder.arrives_when_deal_closes')}</span>
          <span className="num">{sum(atClosing)}</span>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {t('builder.per_payment', { upfront: money(fromUpfront), each: money(fromPayment) })}
        </div>
      </div>

      {/* Что будет при срыве — по шагам.
          Строк ровно столько, сколько платежей: раньше показывались первые
          шесть, и при двадцати платежах продавец видел четверть таблицы
          без единого признака, что она обрезана. Длинные не разворачиваем
          сразу — это метр прокрутки посреди формы, — но разворачиваются
          они целиком и по своей кнопке. */}
      <div className="card col">
        <div className="label">{t('builder.if_buyer_defaults')}</div>
        <table>
          <thead>
            <tr>
              <th>{t('builder.step')}</th>
              <th style={{ textAlign: 'right' }}>{t('builder.you')}</th>
              <th style={{ textAlign: 'right' }}>{t('builder.buyer')}</th>
            </tr>
          </thead>
          <tbody>
            {(allSteps ? rows : rows.slice(0, SHORT_TABLE)).map((r) => (
              <tr key={r.step}>
                <td>{r.step === 0 ? t('counter.down_payment_only') : `+${r.step}`}</td>
                <td style={{ textAlign: 'right' }}>{money(r.lost)}</td>
                <td style={{ textAlign: 'right' }}>{money(r.refunded)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > SHORT_TABLE && (
          <button className="btn btn-ghost btn-sm" onClick={() => setAllSteps(!allSteps)}>
            {allSteps
              ? t('builder.steps_collapse')
              : t('builder.steps_show_all', { count: rows.length - SHORT_TABLE })}
          </button>
        )}
      </div>

      <div className="card row">
        <span className="muted">{t('builder.payment_deferrals')}</span>
        <input type="range" min={0} max={3} value={deferrals} onChange={(e) => setDeferrals(Number(e.target.value))} />
        <span className="num">{deferrals}</span>
      </div>

      {/* Цена переноса не спрашивается, но и не прячется: продавец должен
          знать, за что покупатель доплатит, — иначе первый же вопрос «а
          сколько стоит перенос» уходит в поддержку. Ползунка здесь нет
          намеренно: седьмая ручка на форме стоит внимания дороже, чем
          стоит сама настройка, а разброс цен между офферами мешал бы
          покупателю их сравнивать. */}
      {deferrals > 0 && (
        <div className="card col" style={{ gap: 4 }}>
          <div className="row">
            <span className="muted">{t('builder.defer_price')}</span>
            <span className="num">{sum(terms.deferFee)}</span>
          </div>
          <span className="dim" style={{ fontSize: 11 }}>
            {t('builder.defer_price_hint', { pct: DEFER_FEE_PCT })}
          </span>
        </div>
      )}

      {!enough && (
        <div className="card card-dng text">
          {t('builder.not_enough_gas', { need: sum(CREATE_DEAL_TON), have: sum(balance.data ?? 0n) })}
        </div>
      )}

      {error !== null && <div className="card danger">{error}</div>}
      {problem !== null && <div className="card danger">{t(`err.${problem}`, { min: sum(MIN_PAYMENT) })}</div>}

      {/* По этой вещи оффер уже есть — вторая выкладка только сожжёт газ. */}
      {startedDeal !== undefined && !published && (
        <div className="card col">
          <strong>{t('builder.already_started')}</strong>
          <span className="muted" style={{ fontSize: 13 }}>{t('builder.already_started_hint')}</span>
          <button className="btn" onClick={onPublished}>{t('builder.open_deal')}</button>
        </div>
      )}

      {/* Оффер создан — но гифт ещё у продавца, и до залога покупатели его
          не видят. Раньше здесь всё и кончалось: человек читал «осталось
          передать гифт» и оставался без способа это сделать — кнопка живёт
          на другом экране, на вкладке, которая по умолчанию не открыта.
          Поэтому отсюда ведёт прямая дорога туда. */}
      {published && (
        <div className="card col">
          <strong>{t('builder.published')}</strong>
          <span className="muted" style={{ fontSize: 13 }}>{t('builder.published_hint')}</span>
          <button className="btn" onClick={onPublished}>{t('builder.open_deal')}</button>
        </div>
      )}

      <span className="muted" style={{ fontSize: 12 }}>{t('builder.publish_hint')}</span>

      <button
        className="btn"
        disabled={
          !actions.connected ||
          problem !== null ||
          chosen === null ||
          publishing ||
          !enough ||
          startedDeal !== undefined
        }
        onClick={() => {
          if (chosen === null) return;
          setError(null);
          setPublishing(true);
          setFlowError(null);
          setAwaited(terms.deadline);
          // Индекс берём из контракта айтема, а не из списка: в списке он
          // приходит числом и у больших коллекций теряет точность, а мастер
          // считает по нему адрес — ошибка в цифре означает отказ уже после
          // оплаченного газа.
          void getNftIndex(chosen.address)
            .then((nftIndex) =>
              actions.createDeal({
                masterAddress,
                collection: chosen.collection ?? '',
                nftAddress: chosen.address,
                nftIndex,
                request: {
                  totalPrice: terms.totalPrice,
                  upfront: terms.upfront,
                  deferFee: terms.deferFee,
                  period: terms.period,
                  deadline: terms.deadline,
                  nPayments: terms.nPayments,
                  refundTier: terms.refundTier,
                  maxDeferrals: terms.maxDeferrals,
                },
              }),
            )
            .then(() => {
              setPublished(true);
              setFlow('waiting');
            })
            .catch((e: unknown) => {
              // Отправка транзакции идёт мимо нашего сервера, поэтому о её
              // провале мы узнаём только отсюда.
              // Баланс в отчёт: «не хватило на газ» и «кошелёк сломался»
              // выглядят на экране одинаково, а лечатся по-разному. Здесь
              // он уже прочитан — тот самый, по которому кнопка решала,
              // пускать ли вообще.
              const have = balance.data == null ? 'баланс неизвестен' : `баланс ${balance.data}`;
              reportFailure('создание оффера', e, `${lastWalletRequest()} · ${have}`);
              const text = errorText(e);
              if (text.length > 0) setError(text);
              setFlow(null);
            })
            .finally(() => setPublishing(false));
        }}
      >
        {publishing ? t('builder.publishing') : t('action.publish_offer')}
      </button>

      <GasNote kind="create" />
      <GasNote kind="lock" />

      {/* Шторка статуса — из макета: пока идёт цепочка подписей, человек
          должен видеть, на каком он шаге. Раньше после первой подписи экран
          не менялся вовсе, и второй шаг оставался незамеченным. */}
      {flow !== null && (
        <Sheet
          title={
            flow === 'waiting'
              ? t('builder.flow_waiting')
              : flow === 'timeout'
                ? t('builder.flow_timeout')
                : flow === 'locking'
                  ? t('builder.flow_locking')
                  : flow === 'locked'
                    ? t('state.waiting_upfront')
                    : t('builder.published')
          }
          // Кнопка есть только там, где от человека ждут действия.
          confirmLabel={flow === 'ready' ? t('builder.lock_gift') : undefined}
          busy={flow === 'locking'}
          closeLabel={flow === 'ready' || flow === 'timeout' ? t('builder.later') : undefined}
          onConfirm={() => void lock()}
          onClose={closeFlow}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            {flow === 'waiting'
              ? t('builder.flow_waiting_hint')
              : flow === 'timeout'
                ? t('builder.flow_timeout_hint')
                : flow === 'locking'
                  ? t('state.sent_waiting_hint')
                  : flow === 'locked'
                    ? t('state.waiting_upfront_hint')
                    : t('builder.published_hint')}
          </span>
          {/* Тонкая полоска вместо крутилки: то же самое, но не кричит. */}
          {(flow === 'waiting' || flow === 'locking') && (
            <div className="skeleton" style={{ minHeight: 6, height: 6, borderRadius: 999 }} />
          )}
          {flowError !== null && (
            <div className="danger" style={{ fontSize: 12 }}>{flowError}</div>
          )}
        </Sheet>
      )}
    </div>
  );
}
